import { ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useVideoHealth } from '../hooks/useVideoHealth';
import { getDateRangeFromPreset, useVideoStats } from '../hooks/useVideoStats';
import { CompletionChart, DurationStats } from '../../components/stats';

interface KpiCardProps {
  label: string;
  value: number | string;
  tone: 'amber' | 'slate' | 'green' | 'red' | 'indigo';
  icon: ReactNode;
  hint?: string;
}

const TONE_STYLES: Record<KpiCardProps['tone'], { bg: string; iconBg: string; iconText: string; valueText: string }> = {
  amber: { bg: 'bg-white', iconBg: 'bg-amber-50', iconText: 'text-amber-600', valueText: 'text-slate-900' },
  slate: { bg: 'bg-white', iconBg: 'bg-slate-100', iconText: 'text-slate-600', valueText: 'text-slate-900' },
  green: { bg: 'bg-white', iconBg: 'bg-green-50', iconText: 'text-green-600', valueText: 'text-slate-900' },
  red: { bg: 'bg-white', iconBg: 'bg-red-50', iconText: 'text-red-600', valueText: 'text-slate-900' },
  indigo: { bg: 'bg-white', iconBg: 'bg-indigo-50', iconText: 'text-indigo-600', valueText: 'text-slate-900' },
};

function KpiCard({ label, value, tone, icon, hint }: KpiCardProps) {
  const t = TONE_STYLES[tone];
  return (
    <div
      className={`${t.bg} rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500">{label}</div>
          <div className={`mt-2 text-3xl font-semibold tracking-tight ${t.valueText}`}>{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
        <div
          className={`w-10 h-10 rounded-xl border border-slate-200 ${t.iconBg} ${t.iconText} flex items-center justify-center`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

/**
 * Video service Overview page — mirrors lightning's
 * `web/src/pages/OverviewPage.tsx` layout 1:1 (4 KPI cards + last-7-days
 * mini stats), wired to the video backend's `/health` and stats hooks.
 *
 * The KPI cards read from `health.counts` (the video service exposes
 * counts as a nested object rather than the lightning flat shape),
 * which means the only structural difference from lightning is the
 * source of the numbers — the visual layout stays identical so users
 * switching modes get a consistent overview experience.
 */
export function VideoOverviewPage() {
  const { health } = useVideoHealth();
  const dateRange = useMemo(() => getDateRangeFromPreset('last7days'), []);
  const { stats, loading } = useVideoStats(dateRange);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Active"
          value={health?.counts.processing ?? '—'}
          tone="amber"
          hint="Currently processing"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
        <KpiCard
          label="Pending"
          value={health?.counts.pending ?? '—'}
          tone="slate"
          hint="Waiting in queue"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <KpiCard
          label="Complete"
          value={health?.counts.completed ?? '—'}
          tone="green"
          hint="All-time"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          }
        />
        <KpiCard
          label="Failed"
          value={health?.counts.failed ?? '—'}
          tone="red"
          hint="All-time"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          }
        />
      </div>

      {/* Last 7 days mini stats — same panel lightning shows. */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Last 7 days</h2>
            <p className="text-sm text-slate-500">Completion rate and processing duration</p>
          </div>
          <Link
            to="/video/stats"
            className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors"
          >
            View full statistics
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {loading && !stats ? (
          <div className="p-12 text-center text-sm text-slate-500">Loading statistics…</div>
        ) : stats && stats.completion.total > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
              <CompletionChart stats={stats.completion} title="Completion" />
            </div>
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
              <DurationStats stats={stats.duration} />
            </div>
          </div>
        ) : (
          <div className="p-10 text-center">
            <div className="inline-flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
                <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-slate-900 font-medium">No data yet</p>
                <p className="text-sm text-slate-500">
                  Statistics will appear after the first video scans complete
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
